import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { createNewProtocol } from '../../../../protocolSlice';
import ProtocolBlockBuilder from './ProtocolBlockBuilder';
import DocumentUploadDialog from '../../dialogs/components/DocumentUploadDialog';
import './CreateProtocolForm.scss';

const CreateProtocolForm: React.FC = () => {
  const dispatch = useAppDispatch() as any;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { creating, error } = useAppSelector((state) => state.protocols);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    specialty: '',
    instructions: '',
  });

  const [jsonProtocol, setJsonProtocol] = useState('');
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfConfidence, setPdfConfidence] = useState<number | null>(null);

  // Обработка данных из URL при переходе с документа
  useEffect(() => {
    const fromDocument = searchParams.get('fromDocument');
    const confidence = searchParams.get('confidence');

    if (fromDocument === 'true') {
      try {
        // Получаем данные из sessionStorage
        const documentData = sessionStorage.getItem('documentProtocolData');
        const storedConfidence = sessionStorage.getItem('documentConfidence');

        if (documentData) {
          // Парсим JSON протокол из строки
          const parsedData = JSON.parse(documentData);

          // Автозаполнение формы данными из документа
          if (parsedData.meta) {
            setFormData(prev => ({
              ...prev,
              specialty: parsedData.meta.specialty || '',
              name: parsedData.meta.document_type || 'Протокол из документа',
              description: `Протокол создан автоматически из документа ${storedConfidence ? `(точность: ${Math.round(parseFloat(storedConfidence) * 100)}%)` : ''}`
            }));
          }

          // Устанавливаем JSON протокол в правильном формате (без экранирования)
          setJsonProtocol(JSON.stringify(parsedData, null, 2));

          if (storedConfidence) {
            setPdfConfidence(parseFloat(storedConfidence));
          }

          // Очищаем данные из sessionStorage
          sessionStorage.removeItem('documentProtocolData');
          sessionStorage.removeItem('documentConfidence');
        }
      } catch (error) {
        console.error('Error parsing document data from sessionStorage:', error);
      }
    }
  }, [searchParams]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleJsonChange = (value: string) => {
    // Не перезаписываем JSON, если это пустая строка и у нас уже есть загруженный протокол
    if (value.trim() || !jsonProtocol.trim()) {
      setJsonProtocol(value);
    }
  };

  const handlePdfSuccess = (jsonData: string) => {
    try {
      // Парсим JSON протокол из ответа backend
      const parsedData = JSON.parse(jsonData);

      // Автозаполнение формы данными из документа
      if (parsedData.meta) {
        setFormData(prev => ({
          ...prev,
          specialty: parsedData.meta.specialty || prev.specialty,
          name: parsedData.meta.document_type || 'Протокол из документа',
          description: prev.description || 'Протокол создан автоматически из документа'
        }));
      }

      // Устанавливаем JSON протокол в правильном формате (без экранирования)
      setJsonProtocol(JSON.stringify(parsedData, null, 2));
      setShowPdfDialog(false);
    } catch (error) {
      console.error('Error parsing document data:', error);
    }
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !jsonProtocol.trim()) {
      return;
    }

    try {
      const protocolData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        content: jsonProtocol, // Заменили json_protocol на content
      };

      await dispatch(createNewProtocol(protocolData));
      navigate('/protocols');
    } catch (error) {
      console.error('Failed to create protocol:', error);
    }
  };

  const isFormValid = formData.name.trim() && jsonProtocol.trim();

  return (

    <div className="page-content">
      <form onSubmit={handleSubmit} className="protocol-form">
        <div className="create-protocol-header">
          <div className="header-left">
            <h1>Создание протокола</h1>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="button-secondary pdf-upload-btn"
              onClick={() => setShowPdfDialog(true)}
              disabled={creating}
            >
              📄 Загрузить документ
            </button>
            <button
              type="button"
              className="button-secondary"
              onClick={() => navigate('/protocols')}
              disabled={creating}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="button-primary"
              disabled={!isFormValid || creating}
            >
              {creating ? 'Создание...' : 'Создать протокол'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {pdfConfidence !== null && (
          <div className="pdf-info-banner">
            <div className="pdf-info-content">
              <span className="pdf-icon">📄</span>
              <div className="pdf-info-text">
                <strong>Протокол создан из документа</strong>
                <span className="confidence-score">
                  Точность распознавания: {Math.round(pdfConfidence * 100)}%
                </span>
              </div>
              <button
                type="button"
                className="close-banner"
                onClick={() => setPdfConfidence(null)}
              >
                ✕
              </button>
            </div>
          </div>
        )}
        <div className="form-layout">
          {/* Левая колонка - информация о протоколе */}
          <div className="form-info-section">
            <div className="form-section mb-3">
              <h2>Основная информация</h2>

              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Название протокола *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="search-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Введите название протокола"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Описание
                </label>
                <textarea
                  id="description"
                  name="description"
                  className="search-input textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Краткое описание протокола"
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label htmlFor="specialty" className="form-label">
                  Специальность
                </label>
                <input
                  type="text"
                  id="specialty"
                  name="specialty"
                  className="search-input"
                  value={formData.specialty}
                  onChange={handleInputChange}
                  placeholder="Укажите медицинскую специальность (например: терапия, кардиология)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="instructions" className="form-label">
                  Общие инструкции по протоколу
                </label>
                <textarea
                  id="instructions"
                  name="instructions"
                  className="search-input textarea"
                  value={formData.instructions}
                  onChange={handleInputChange}
                  placeholder="Введите общие инструкции для заполнения протокола (x-instructions)"
                  rows={6}
                />
              </div>
            </div>

            <div className="form-section">
              <h2>Подсказки</h2>
              <div className="tips-container">
                <div className="tip-item">
                  <span className="tip-text">Используйте блоки для группировки связанных полей</span>
                </div>
                <div className="tip-item">
                  <span className="tip-text">Настройте типы полей в зависимости от данных</span>
                </div>
                <div className="tip-item">
                  <span className="tip-text">Отмечайте обязательные поля для валидации</span>
                </div>
              </div>
            </div>
          </div>

          {/* Правая колонка - конструктор */}
          <div className="form-builder-section">

            <ProtocolBlockBuilder
              onBlocksChange={() => { }}
              onJsonChange={handleJsonChange}
              specialty={formData.specialty}
              instructions={formData.instructions}
              existingJsonProtocol={jsonProtocol}
            />
          </div>

        </div>
      </form>

      <DocumentUploadDialog
        isOpen={showPdfDialog}
        onClose={() => setShowPdfDialog(false)}
        onSuccess={handlePdfSuccess}
      />
    </div>
  );
};

export default CreateProtocolForm;
